import { iconV2Options } from '@/components/icons/utils/IconOptions'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { useCurrentApplication } from '../useCurrentApplication'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button } from '@/components/shadcn/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/shadcn/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Textarea } from '@/components/shadcn/textarea'
import { MultiSelectComboboxField } from '@/components/form-components/MultiSelectCombobox'
import { SingleSelectCombobox } from '@/components/form-components/SingleSelectCombobox'
import { DynamicIcon } from '@/components/icons/DynamicIcon'
import { DynamicIconName } from '@/components/icons/types'
import { IconHelpQuestionOutline } from '@icons/IconHelpQuestionOutline'
import PageForm, { TPageForm } from '../Forms/PageForm'

type MessagesLandingMenuItem = RestSchemas['Messages.Landing.MenuItem']

const formSchema = z.object({
  item_type: z.string().min(1, 'Item type is required'),
  name: z.string().min(1, 'Name is required'),
  description: z.string().min(1, 'Description is required'),
  page_id: z.string().min(1, 'Page ID is required'),
  icon_name: z.string().optional(),
  assignedRoles: z.array(z.string()).default([]),
})

export type MenuItemFormValues = z.infer<typeof formSchema>

type MenuItemFormProps = {
  onSubmit: (data: MenuItemFormValues) => void
  initialValues?: Omit<MessagesLandingMenuItem, 'items'>
  showIcon?: boolean
  closeSheetOnSubmit?: boolean
}

export const MenuItemForm = ({ onSubmit, initialValues, showIcon, closeSheetOnSubmit }: MenuItemFormProps) => {
  const { applicationId } = useCurrentApplication()
  const [createPageDialogOpen, setCreatePageDialogOpen] = useState(false)
  const [createdPageIds, setCreatedPageIds] = useState<string[]>([])

  const { getApplicationsApiListApplicationRoles, getUiApiGetPages } = useRestClient()

  const {
    data: availablePages,
    isError: availablePagesError,
    isPending: availablePagesPending,
  } = getUiApiGetPages.useQuery(
    {
      path: { applicationId },
    },
    {
      select: (data) => data.filter((page) => Boolean(page.page)).map((page) => page.page as string),
    }
  )

  const {
    data: fetchedRoles,
    isError: fetchedRolesError,
    isPending: fetchedRolesPending,
  } = getApplicationsApiListApplicationRoles.useQuery(
    {
      query: { applicationId },
    },
    {
      select: (data) => data.map((role) => role.name),
    }
  )

  const defaultValues = useMemo<MenuItemFormValues>(
    () => ({
      item_type: initialValues?.item_type || 'menu_item',
      name: initialValues?.name ?? '',
      description: initialValues?.description ?? '',
      page_id: initialValues?.page_id ?? '',
      icon_name: initialValues?.icon_name ?? '',
      assignedRoles: initialValues?.roles ?? [],
    }),
    [initialValues]
  )

  const form = useForm<MenuItemFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })
  const menuItemDescription = form.watch('description').trim()

  const pageOptions = useMemo(() => {
    const pageIds = [...(availablePages ?? []), ...createdPageIds]
    const uniquePageIds = Array.from(new Set(pageIds))

    return uniquePageIds.map((page) => ({ label: page, value: page }))
  }, [availablePages, createdPageIds])

  const handlePageCreated = useCallback(
    (page: TPageForm) => {
      const pageId = page.id.trim()

      if (!pageId) {
        return
      }

      setCreatedPageIds((currentPageIds) => (currentPageIds.includes(pageId) ? currentPageIds : [...currentPageIds, pageId]))
      form.setValue('page_id', pageId, { shouldDirty: true, shouldValidate: true })
      getUiApiGetPages.invalidateQueries({ parameters: { path: { applicationId } } })
      setCreatePageDialogOpen(false)
    },
    [applicationId, form, getUiApiGetPages]
  )

  const roleOptions = (fetchedRoles ?? []).map((role) => ({ label: role, value: role }))
  const iconOptions = iconV2Options.map((iconName) => ({ label: iconName, value: iconName }))

  if (!applicationId) {
    return <div>No app ID</div>
  }

  if (availablePagesPending || fetchedRolesPending) {
    return <div>Loading...</div>
  }

  if (availablePagesError || fetchedRolesError) {
    return <div>Error</div>
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex h-full flex-col">
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="item_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Item Type</FormLabel>
                  <FormControl>
                    <Input {...field} readOnly />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={4} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="page_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Page ID</FormLabel>
                  <SingleSelectCombobox
                    options={pageOptions}
                    selectedValue={field.value}
                    onSelectionChange={(value) => field.onChange(value ?? '')}
                    placeholder="Select a page"
                    searchPlaceholder="Search pages..."
                    emptyMessage="No pages found."
                    allowClear={false}
                    footerContent={(closePopover) => (
                      <Button
                        type="button"
                        variant="ghost"
                        className="w-full justify-start"
                        onClick={() => {
                          closePopover()
                          setCreatePageDialogOpen(true)
                        }}
                      >
                        Create new page
                      </Button>
                    )}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="icon_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Icon</FormLabel>
                  <FormControl>
                    <SingleSelectCombobox
                      options={iconOptions}
                      selectedValue={field.value || null}
                      onSelectionChange={(value) => field.onChange(value ?? '')}
                      placeholder="Select an icon"
                      searchPlaceholder="Search icons..."
                      emptyMessage="No icons found."
                      disabled={!showIcon}
                      renderSelectedOption={(option) => <IconOptionContent iconName={option.value} label={option.label} />}
                      renderOption={(option) => <IconOptionContent iconName={option.value} label={option.label} />}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <MultiSelectComboboxField
              control={form.control}
              name="assignedRoles"
              label="Assigned Roles"
              options={roleOptions}
              placeholder="Select roles"
              searchPlaceholder="Search roles..."
            />
          </div>

          <div className="form-action-container">
            <Button type="submit">{closeSheetOnSubmit ? 'Submit' : 'Save'}</Button>
          </div>
        </div>
      </form>
      <Dialog open={createPageDialogOpen} onOpenChange={setCreatePageDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create new page</DialogTitle>
            <DialogDescription>Add a page to this application and link it to this menu item.</DialogDescription>
          </DialogHeader>
          <PageForm
            initialValues={menuItemDescription ? { description: menuItemDescription } : undefined}
            onSubmit={handlePageCreated}
          />
        </DialogContent>
      </Dialog>
    </Form>
  )
}

function IconOptionContent({ iconName, label }: { iconName: string; label: string }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <DynamicIcon
        iconName={iconName as DynamicIconName}
        size={24}
        className="shrink-0"
        wrapperClassName="flex size-6 shrink-0 items-center justify-center"
        fallback={<IconHelpQuestionOutline size={24} className="shrink-0" />}
        loadingFallback={<span className="size-6 shrink-0" />}
      />
      <span className="min-w-0 truncate font-medium">{label}</span>
    </div>
  )
}
